/**
 * Client-Side Payment Calculator Utility
 * Calculates payment schedules for contracts (mirrors backend logic)
 */

/**
 * Calculate the number of payment periods between two dates
 */
function calculatePeriods(startDate, endDate, frequency) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (frequency === 'one-time') {
    return 1;
  }
  
  if (frequency === 'twice') {
    return 2;
  }
  
  if (frequency === 'twice-yearly') {
    // Calculate how many May 20 and Dec 20 dates fall within the contract period
    let count = 0;
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();
    
    for (let year = startYear; year <= endYear; year++) {
      // Check May 20
      const mayDate = new Date(year, 4, 20); // Month 4 = May (0-indexed)
      if (mayDate >= start && mayDate <= end) {
        count++;
      }
      
      // Check December 20
      const decDate = new Date(year, 11, 20); // Month 11 = December (0-indexed)
      if (decDate >= start && decDate <= end) {
        count++;
      }
    }
    
    return count; // Return actual count, even if 0
  }
  
  const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 +
                     (end.getMonth() - start.getMonth());
  
  switch (frequency) {
    case 'monthly':
      return Math.max(1, monthsDiff + 1);
    case 'quarterly':
      // If contract <= 12 months, return 4 fixed payments
      if (monthsDiff <= 12) {
        return 4;
      }
      // Otherwise, return number of quarters in period
      return Math.max(1, Math.floor(monthsDiff / 3) + 1);
    default:
      return 1;
  }
}

/**
 * Adjust payment day for months with fewer days
 */
function adjustPaymentDay(year, month, dayOfMonth) {
  // Get the last day of the month
  const lastDay = new Date(year, month + 1, 0).getDate();
  
  // If requested day is greater than days in month, use last day
  return Math.min(dayOfMonth, lastDay);
}

/**
 * Calculate a specific payment date
 */
function getPaymentDate(startDate, endDate, periodIndex, frequency, dayOfMonth) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  let paymentDate = new Date(start);
  
  if (frequency === 'one-time') {
    // For one-time, use the start date with adjusted day
    const adjustedDay = adjustPaymentDay(
      paymentDate.getFullYear(),
      paymentDate.getMonth(),
      dayOfMonth
    );
    paymentDate.setDate(adjustedDay);
    return paymentDate.toISOString().split('T')[0];
  }
  
  // Handle 'twice' frequency
  if (frequency === 'twice') {
    if (periodIndex === 0) {
      // First payment at start date
      const adjustedDay = adjustPaymentDay(
        paymentDate.getFullYear(),
        paymentDate.getMonth(),
        dayOfMonth
      );
      paymentDate.setDate(adjustedDay);
      return paymentDate.toISOString().split('T')[0];
    } else {
      // Second payment at halfway point
      const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      const halfwayDays = Math.floor(totalDays / 2);
      paymentDate.setDate(paymentDate.getDate() + halfwayDays);
      const adjustedDay = adjustPaymentDay(
        paymentDate.getFullYear(),
        paymentDate.getMonth(),
        dayOfMonth
      );
      paymentDate.setDate(adjustedDay);
      return paymentDate.toISOString().split('T')[0];
    }
  }
  
  // Handle 'twice-yearly' frequency - fixed May 20 and December 20 dates
  if (frequency === 'twice-yearly') {
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();
    const paymentDates = [];
    
    // Collect all May 20 and Dec 20 dates within contract period
    for (let year = startYear; year <= endYear; year++) {
      const mayDate = new Date(year, 4, 20); // May 20
      if (mayDate >= start && mayDate <= end) {
        paymentDates.push(mayDate);
      }
      
      const decDate = new Date(year, 11, 20); // December 20
      if (decDate >= start && decDate <= end) {
        paymentDates.push(decDate);
      }
    }
    
    // Sort dates chronologically
    paymentDates.sort((a, b) => a - b);
    
    // Return the date at the specified index
    if (periodIndex < paymentDates.length) {
      return paymentDates[periodIndex].toISOString().split('T')[0];
    }
    
    // Fallback to start date if index out of bounds
    return start.toISOString().split('T')[0];
  }
  
  // Handle 'quarterly' frequency with conditional logic
  if (frequency === 'quarterly') {
    const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 +
                       (end.getMonth() - start.getMonth());
    
    if (monthsDiff <= 12) {
      // Contract <= 1 year: distribute 4 payments evenly
      const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      const intervalDays = totalDays / 4;
      paymentDate.setDate(paymentDate.getDate() + Math.floor(intervalDays * periodIndex));
      const adjustedDay = adjustPaymentDay(
        paymentDate.getFullYear(),
        paymentDate.getMonth(),
        dayOfMonth
      );
      paymentDate.setDate(adjustedDay);
      return paymentDate.toISOString().split('T')[0];
    } else {
      // Contract > 1 year: calendar-based quarters
      paymentDate.setMonth(paymentDate.getMonth() + (periodIndex * 3));
      const adjustedDay = adjustPaymentDay(
        paymentDate.getFullYear(),
        paymentDate.getMonth(),
        dayOfMonth
      );
      paymentDate.setDate(adjustedDay);
      return paymentDate.toISOString().split('T')[0];
    }
  }
  
  // Monthly remains calendar-based
  if (frequency === 'monthly') {
    paymentDate.setMonth(paymentDate.getMonth() + periodIndex);
    const adjustedDay = adjustPaymentDay(
      paymentDate.getFullYear(),
      paymentDate.getMonth(),
      dayOfMonth
    );
    paymentDate.setDate(adjustedDay);
    return paymentDate.toISOString().split('T')[0];
  }
  
  return paymentDate.toISOString().split('T')[0];
}

/**
 * Generate complete payment schedule for a contract
 */
function generatePaymentSchedule(startDate, endDate, totalAmount, frequency, dayOfMonth = 25) {
  const periods = calculatePeriods(startDate, endDate, frequency);
  const amountPerPeriod = totalAmount / periods;
  
  const payments = [];
  
  for (let i = 0; i < periods; i++) {
    const scheduledDate = getPaymentDate(startDate, endDate, i, frequency, dayOfMonth);
    
    // Round to 2 decimal places
    const amount = Math.round(amountPerPeriod * 100) / 100;
    
    payments.push({
      payment_number: i + 1,
      scheduled_date: scheduledDate,
      amount: amount,
      status: 'scheduled'
    });
  }
  
  // Adjust last payment to account for rounding
  if (payments.length > 0) {
    const totalCalculated = payments.reduce((sum, p) => sum + p.amount, 0);
    const difference = Math.round((totalAmount - totalCalculated) * 100) / 100;
    if (difference !== 0) {
      payments[payments.length - 1].amount += difference;
      payments[payments.length - 1].amount = Math.round(payments[payments.length - 1].amount * 100) / 100;
    }
  }
  
  return payments;
}

/**
 * Main entry point - Calculate payments for a contract
 */
export function calculatePayments(contract) {
  const {
    start_date,
    end_date,
    total_amount,
    payment_frequency,
    payment_day_of_month = 25
  } = contract;
  
  // Validate required fields
  if (!start_date || !end_date || !total_amount) {
    return [];
  }
  
  const frequency = payment_frequency || 'monthly';
  const dayOfMonth = parseInt(payment_day_of_month) || 25;
  
  return generatePaymentSchedule(
    start_date,
    end_date,
    parseFloat(total_amount),
    frequency,
    dayOfMonth
  );
}

export default {
  calculatePayments,
  calculatePeriods,
  adjustPaymentDay,
  getPaymentDate,
  generatePaymentSchedule
};